(() => {
  if (window.CLCBulkPricing) return;
  const tiers = Object.freeze([{min:12,discount:10},{min:16,discount:15},{min:24,discount:25},{min:32,discount:30},{min:40,discount:35},{min:48,discount:40}]);
  const rateFor = pieces => tiers.reduce((rate, tier) => pieces >= tier.min ? tier.discount : rate, 0);
  const piecesIn = items => items.reduce((total, item) => item.gift_card ? total : total + item.quantity * (Number(item.pieces_per_unit) || 1), 0);
  const isBulkTitle = title => typeof title === 'string' && (
    title.replace(/&amp;/g, '&') === 'Mix & Match Bulk Savings' ||
    tiers.some(tier => title === `${tier.min}+ pack · ${tier.discount}% off`)
  );
  const bulkSavingsIn = cart => {
    const lineSavings = (cart.items || []).flatMap(item => item.line_level_discount_allocations || [])
      .filter(allocation => isBulkTitle(allocation.discount_application?.title))
      .reduce((total, allocation) => total + (Number(allocation.amount) || 0), 0);
    return lineSavings + (cart.cart_level_discount_applications || [])
      .filter(discount => isBulkTitle(discount.title))
      .reduce((total, discount) => total + (Number(discount.total_allocated_amount) || 0), 0);
  };
  const discountFeedback = (cart, code, locale = document.documentElement.lang || 'en') => {
    const result = (cart.discount_codes || []).find(discount => discount.code.toLowerCase() === code.toLowerCase());
    if (result?.applicable) return `Code ${code} applied. Your cart shows the best eligible savings.`;
    const savings = bulkSavingsIn(cart);
    if (savings > 0) {
      return 'Bulk savings kept. Code not applied.';
    }
    return 'This code could not be applied. Check its spelling, expiry date and product eligibility.';
  };
  const quote = (unitPrice, quantity, existingPieces = 0, multiplier = 1) => {
    const pieces = quantity * multiplier, rate = rateFor(existingPieces + pieces), gross = unitPrice * quantity;
    const saving = Math.round(gross * rate / 100);
    return {pieces, rate, gross, saving, total:gross - saving};
  };
  const contextFrom = (root = document) => {
    const node = root.querySelector('[data-clc-pricing-context]');
    if (!node) throw new Error('Cart pricing context unavailable');
    return JSON.parse(node.textContent);
  };
  const refresh = async signal => {
    const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    url.searchParams.set('section_id', 'clc-pricing-context'); url.searchParams.set('_clc', Date.now());
    const response = await fetch(url, {headers:{Accept:'text/html'}, cache:'no-store', signal});
    if (!response.ok) throw new Error('Cart pricing refresh failed');
    const context = contextFrom(new DOMParser().parseFromString(await response.text(), 'text/html'));
    document.dispatchEvent(new CustomEvent('clc:pricing-context', {detail:context}));
    return context;
  };
  window.CLCBulkPricing = Object.freeze({tiers, rateFor, piecesIn, bulkSavingsIn, discountFeedback, quote, contextFrom, refresh});
  let restoredFeedback;
  const restoreFeedback = () => {
    if (restoredFeedback !== undefined) return restoredFeedback;
    restoredFeedback = '';
    try {
      restoredFeedback = sessionStorage.getItem('clc-discount-feedback') || '';
      sessionStorage.removeItem('clc-discount-feedback');
    } catch {}
    return restoredFeedback;
  };
  if (!customElements.get('clc-cart-discount')) {
    customElements.define('clc-cart-discount', class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.abort = new AbortController();
        this.status = this.querySelector('[data-discount-status]');
        const previous = restoreFeedback();
        if (previous) {
          this.status.textContent = previous;
          const disclosure = this.closest('.cart-promo-disclosure');
          if (disclosure) disclosure.open = true;
        }
        if (typeof subscribe === 'function') {
          this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            restoredFeedback = '';
            this.status.textContent = this.status.getAttribute('data-default-status') ?? 'Bulk savings apply automatically. The best eligible offer wins.';
          });
        }
        this.querySelector('[data-remove-discounts]')?.addEventListener('click', () => this.removeCodes(), {signal:this.abort.signal});
        this.querySelector('form').addEventListener('submit', async event => {
          event.preventDefault();
          const form = event.currentTarget, input = form.elements.discount, code = input.value.trim(), buttons = [...this.querySelectorAll('button')];
          if (!code) { this.status.textContent = 'Enter a discount code.'; input.focus(); return; }
          buttons.forEach(button => { button.disabled = true; }); this.status.textContent = 'Checking your code and the best available savings…';
          try {
            const currentResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {headers:{Accept:'application/json'}, cache:'no-store', signal:this.abort.signal});
            if (!currentResponse.ok) throw new Error('Cart unavailable');
            const current = await currentResponse.json();
            const codes = (current.discount_codes || []).filter(discount => discount.applicable).map(discount => discount.code);
            if (!codes.some(existing => existing.toLowerCase() === code.toLowerCase())) codes.push(code);
            const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({discount:codes.join(',')}), signal:this.abort.signal});
            if (!response.ok) throw new Error('Discount update failed');
            const cart = await response.json();
            if (!Array.isArray(cart.items)) throw new Error('Cart pricing unavailable');
            const message = discountFeedback(cart, code);
            try { sessionStorage.setItem('clc-discount-feedback', message); } catch {}
            this.status.textContent = message;
            window.location.reload();
          } catch (error) {
            if (error.name !== 'AbortError') this.status.textContent = 'We couldn’t check your code. Please try again.';
          } finally { buttons.forEach(button => { button.disabled = false; }); }
        }, {signal:this.abort.signal});
      }
      async removeCodes() {
        const buttons = [...this.querySelectorAll('button')];
        buttons.forEach(button => { button.disabled = true; });
        this.status.textContent = 'Removing codes and checking your automatic savings…';
        try {
          const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({discount:''}), signal:this.abort.signal});
          if (!response.ok) throw new Error('Discount removal failed');
          const cart = await response.json();
          if (!Array.isArray(cart.items) || (cart.discount_codes || []).some(code => code.applicable)) throw new Error('Code removal not confirmed');
          const savings = bulkSavingsIn(cart);
          const amount = savings > 0 ? new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:cart.currency, currencyDisplay:'narrowSymbol'}).format(savings / 100) : '';
          const message = savings > 0 ? `Discount codes removed. ${amount} in bulk savings now applies automatically.` : 'Discount codes removed. Eligible automatic savings are reflected in your cart.';
          try { sessionStorage.setItem('clc-discount-feedback', message); } catch {}
          this.status.textContent = message;
          window.location.reload();
        } catch (error) {
          if (error.name !== 'AbortError') this.status.textContent = 'We couldn’t remove your codes. Please try again.';
        } finally { buttons.forEach(button => { button.disabled = false; }); }
      }
      disconnectedCallback() { this.abort?.abort(); this.abort = null; this.cartUnsubscribe?.(); this.cartUnsubscribe = null; }
    });
  }
})();
